import * as client from './client.js';
import * as server from './server.js';
import * as parsers from './parsers.js';
import * as utils from './utils.js';
import * as types from './types.js'
import {AuthenticateOptions, RegisterOptions} from './types.js'
import {authenticatorMetadata} from './authenticatorMetadata.js'
import axios from "axios";
import {BASE_URL} from "./config";
import {exec} from 'child_process';

export function initRandomUUID() {
    if (!window.crypto.randomUUID) {
        window.crypto.randomUUID = function () {
            let arr = new Uint8Array(16);
            window.crypto.getRandomValues(arr);
            arr[6] = (arr[6] & 0x0f) | 0x40; // 版本 4
            arr[8] = (arr[8] & 0x3f) | 0x80; // 变体 1
            let hexArr = Array.from(arr, function (byte) {
                return ('0' + byte.toString(16)).slice(-2);
            });
            return `${hexArr.slice(0, 4).join('')}-${hexArr.slice(4, 6).join('')}-${hexArr.slice(6, 8).join('')}-${hexArr.slice(8, 10).join('')}-${hexArr.slice(10, 16).join('')}`;
        };
    }
}

async function getChallenge(username: string, type: 'register' | 'login') {
    const apiMap = {
        'register': '/fido/registration/challenge',
        'login': '/fido/login/challenge'
    }
    try {
        const response = await axios.get(BASE_URL + apiMap[type], {
            params: {
                username: username
            }
        });
        console.log(response);

        return response.data;
    } catch (e: any) {
        if (e.response) {
            return e.response.data;
        }
        throw e;
    }


}


/**
 * 检查当前浏览器/平台是否支持无密码认证。
 * @returns {boolean} 如果支持，则返回 true，否则返回 false。
 */
async function isAvailable(): Promise<boolean> {
    if(!window.PublicKeyCredential) return false;
    initRandomUUID();
    const challenge = window.crypto.randomUUID();
    const creationOptions: PublicKeyCredentialCreationOptions = {
        challenge: utils.parseBase64url(challenge),
        rp: {
            // id: 'mfa.greeyun.com',
            // name: 'Gree MFA Platform',
            id: window.location.hostname,
            name: window.location.hostname
        },
        user: {
            id: new Uint8Array(32), // 从隐私考虑，ID不应该直接可识别
            name: 'test',
            displayName: 'test'
        },
        pubKeyCredParams: [
            // ES256 （Webauthn的默认算法）
            {alg: -7, type: "public-key"},
            // RS256 （适用于Windows Hello等）
            {alg: -257, type: "public-key"},
        ],
        timeout: 60000,
        authenticatorSelection: {
            // Webauthn的默认值是"preferred"
            userVerification: "required",
            // authenticatorAttachment: await getAuthAttachment(options.authenticatorType ?? "auto"),
            authenticatorAttachment: "platform",
            // 官方默认值是'discouraged'
            residentKey: 'preferred',
            // 主要是为了向后兼容，见https://www.w3.org/TR/webauthn/#dictionary-authenticatorSelection
            requireResidentKey: false
        },
        attestation: "direct"
    }

    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
}




async function register(username: string, challenge?: string, isRoaming?: boolean, options?: types.RegisterOptions) {
    initRandomUUID();

    try {
        options = options ?? {};
        isRoaming = isRoaming ?? false;
        const challengeResponse = await getChallenge(username, 'register');

        challenge = challenge ?? challengeResponse.data.Session.challenge ?? window.crypto.randomUUID();

        let res = await client.register(username, challenge, {
            ...options,
            authenticatorType: isRoaming ? 'roaming' : 'auto'
        });
        // console.log(res);
        const posData = {
            ...res.credential,
            deviceInfo: res.deviceInfo,
            username
        }
        const resultRes = await axios.post(BASE_URL + '/fido/registration', posData);
        localStorage.setItem('deviceInfo', res.deviceInfo)
        return resultRes.data;
    } catch (e: any) {
        if (e.response) {
            return e.response.data;
        }
        throw e;
    }
}

async function login(username: string, challenge?: string, isRoaming?: boolean, loginOptions?: AuthenticateOptions) {
    initRandomUUID();
    try {
        isRoaming = isRoaming ?? false;
        loginOptions = loginOptions ?? {};

        const challengeResponse = await getChallenge(username, 'login');

        challenge = challenge ?? challengeResponse.data.Session.challenge ?? window.crypto.randomUUID();

        const ids = challengeResponse.data.Assertion.publicKey.allowCredentials.map((item: any) => item.id);

        let response = await client.authenticate(ids ? ids : [], challenge as string, {
            ...loginOptions,
            authenticatorType: isRoaming ? 'roaming' : 'auto'
        })

        // console.log(response)
        const posData = {
            ...response,
            username
        }
        const resultRes = await axios.post(BASE_URL + '/fido/login', posData);
        return resultRes.data;
    } catch (e: any) {
        if (e.response) {
            return e.response.data;
        }
        throw e;
    }
}

async function isRegistered(username: string) {
    try {
        const response = await getChallenge(username, 'login');
        // console.log(response)
        if (response.code === 200) {
            return true;
        }
        return false;
    } catch (e: any) {
        if (e.response) {
            return false;
        }
        throw e;
    }
}


async function checkIdRegistered(username: string, api: string) {
    return await client.checkIdRegistered(username, api);
}

async function getDeviceCommonUser() {
    const deviceInfo = localStorage.getItem('deviceInfo');
    if (deviceInfo) {
        const res = await axios.get(BASE_URL + '/behavioral/device/userName',{
            params: {
                deviceInfo
            }
        });
        return res.data.data.username;
    } else {
        return null;
    }
}

export {
    client,
    server,
    parsers,
    utils,
    authenticatorMetadata,
    register,
    login,
    checkIdRegistered,
    getChallenge,
    isAvailable,
    isRegistered,
    getDeviceCommonUser
}

export default {
    client,
    server,
    parsers,
    utils,
    authenticatorMetadata,
    register,
    login,
    checkIdRegistered,
    getChallenge,
    isAvailable,
    isRegistered,
    getDeviceCommonUser
}